import L from "leaflet";

const STATE_KEY = "hydrotools:historical-weather:v1";

const CHART_SPECS = [
  { key: "temp", id: "hist-chart-temp", title: "Temperature", unit: "F", color: "#f97316", axis: { step: 10 } },
  { key: "wind", id: "hist-chart-wind", title: "Wind Speed", unit: "kt", color: "#22c55e", axis: { step: 5, min: 0 } },
  { key: "wave", id: "hist-chart-wave", title: "Wave Height", unit: "ft", color: "#38bdf8", axis: { step: 1, min: 0, maxDefault: 6 } },
  { key: "daylight", id: "hist-chart-daylight", title: "Daylight Duration", unit: "hr", color: "#eab308", axis: { step: 4, min: 0, max: 24 } },
];

const KMH_TO_KT = 0.539956803;
const M_TO_FT = 3.280839895;
const C_TO_F_SCALE = 9 / 5;
const C_TO_F_OFFSET = 32;

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function readState() {
  const nowYear = new Date().getUTCFullYear();
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const x = raw ? JSON.parse(raw) : {};
    return {
      lat: Number.isFinite(Number(x.lat)) ? Number(x.lat) : 29.75,
      lon: Number.isFinite(Number(x.lon)) ? Number(x.lon) : -94.85,
      startYear: Number.isFinite(Number(x.startYear)) ? clamp(Number(x.startYear), 1940, nowYear) : nowYear - 9,
      endYear: Number.isFinite(Number(x.endYear)) ? clamp(Number(x.endYear), 1940, nowYear) : nowYear,
    };
  } catch {
    return { lat: 29.75, lon: -94.85, startYear: nowYear - 9, endYear: nowYear };
  }
}

function saveState(s) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch {}
}

function buildDoyKeys() {
  const keys = [];
  for (let m = 1; m <= 12; m += 1) {
    const days = new Date(Date.UTC(2021, m, 0)).getUTCDate();
    for (let d = 1; d <= days; d += 1) {
      const mm = String(m).padStart(2, "0");
      const dd = String(d).padStart(2, "0");
      keys.push(`${mm}-${dd}`);
    }
  }
  return keys;
}

const DOY_KEYS = buildDoyKeys();
const MONTH_LABELS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const MONTH_STARTS = (() => {
  const out = [];
  let idx = 0;
  for (let m = 1; m <= 12; m += 1) {
    out.push(idx);
    idx += new Date(Date.UTC(2021, m, 0)).getUTCDate();
  }
  return out;
})();

function aggregateDailyTo365(times = [], values = []) {
  const buckets = new Map(DOY_KEYS.map((k) => [k, { sum: 0, count: 0 }]));
  const n = Math.min(times.length, values.length);
  for (let i = 0; i < n; i += 1) {
    const t = String(times[i] || "");
    const key = t.slice(5, 10);
    if (key === "02-29") continue;
    if (!buckets.has(key)) continue;
    const v = Number(values[i]);
    if (!Number.isFinite(v)) continue;
    const b = buckets.get(key);
    b.sum += v;
    b.count += 1;
  }
  return DOY_KEYS.map((k) => {
    const b = buckets.get(k);
    return b.count > 0 ? (b.sum / b.count) : null;
  });
}

function stats(values = []) {
  const valid = values.filter((v) => Number.isFinite(v));
  if (!valid.length) return { min: null, max: null, avg: null };
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const avg = valid.reduce((a, b) => a + b, 0) / valid.length;
  return { min, max, avg };
}

function formatDayLabel(dayIdx) {
  const i = clamp(Math.round(Number(dayIdx) || 0), 0, DOY_KEYS.length - 1);
  const key = DOY_KEYS[i];
  const mm = Number(key.slice(0, 2));
  const dd = key.slice(3, 5);
  return `${MONTH_LABELS[mm - 1]} ${dd}`;
}

function formatHoverValue(v, unit) {
  if (!Number.isFinite(v)) return `-- ${unit}`;
  return `${Number(v).toFixed(2)} ${unit}`;
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (Number(d) * Math.PI) / 180;
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dp = toRad(lat2 - lat1);
  const dl = toRad(lon2 - lon1);
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 6371 * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

function formatDistanceNm(km) {
  if (!Number.isFinite(km)) return "--";
  const nm = km / 1.852;
  return `${nm.toFixed(1)} nm`;
}

function fmtUtcDate(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildAxis(values, axis = {}) {
  const step = Math.max(1, Math.round(Number(axis.step) || 1));
  const valid = values.filter((v) => Number.isFinite(v));
  let min = Number.isFinite(Number(axis.min)) ? Number(axis.min) : 0;
  let max = Number.isFinite(Number(axis.max)) ? Number(axis.max) : 0;

  if (valid.length) {
    const vmin = Math.min(...valid);
    const vmax = Math.max(...valid);
    if (!Number.isFinite(Number(axis.min))) min = Math.floor(vmin / step) * step;
    if (!Number.isFinite(Number(axis.max))) max = Math.ceil(vmax / step) * step;
  } else {
    if (!Number.isFinite(Number(axis.max))) max = min + (step * 4);
  }

  if (Number.isFinite(Number(axis.maxDefault))) max = Math.max(max, Number(axis.maxDefault));
  if (max <= min) max = min + (step * 4);

  const ticks = [];
  for (let v = max; v >= min; v -= step) ticks.push(v);
  if (ticks[ticks.length - 1] !== min) ticks.push(min);
  return { min, max, step, ticks };
}

function drawBars(canvas, values, opts = {}) {
  if (!canvas) return null;
  const color = opts.color || "#60a5fa";
  const unit = opts.unit || "";
  const hoverIndex = Number.isFinite(opts.hoverIndex) ? opts.hoverIndex : -1;
  const axis = buildAxis(values, opts.axis || {});

  const widthCss = Math.max(360, canvas.clientWidth || 720);
  const heightCss = 132;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(widthCss * dpr);
  canvas.height = Math.round(heightCss * dpr);
  canvas.style.height = `${heightCss}px`;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, widthCss, heightCss);

  const left = 46;
  const top = 10;
  const right = 8;
  const bottom = 30;
  const w = widthCss - left - right;
  const h = heightCss - top - bottom;

  const valid = values.filter((v) => Number.isFinite(v));
  if (!valid.length) return { left, top, right, bottom, w, h, bw: w / Math.max(1, values.length), count: values.length };
  const min = axis.min;
  const max = axis.max;

  ctx.fillStyle = "rgba(148,163,184,0.16)";
  ctx.font = "10px sans-serif";
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  for (const tick of axis.ticks) {
    const frac = (tick - min) / (max - min);
    const y = top + h - (frac * h);
    ctx.fillRect(left, y, w, 1);
    ctx.fillStyle = "rgba(148,163,184,0.85)";
    ctx.fillText(`${Math.round(tick)} ${unit}`.trim(), left - 4, y);
    ctx.fillStyle = "rgba(148,163,184,0.16)";
  }

  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  for (let m = 0; m < MONTH_STARTS.length; m += 1) {
    const dayIdx = MONTH_STARTS[m];
    const x = left + (dayIdx / values.length) * w;
    ctx.fillStyle = "rgba(148,163,184,0.24)";
    ctx.fillRect(x, top, 1, h);
    ctx.fillStyle = "rgba(148,163,184,0.92)";
    ctx.fillText(MONTH_LABELS[m], x + 11, top + h + 4);
  }

  const bw = w / values.length;
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (!Number.isFinite(v)) continue;
    const t = clamp((v - min) / (max - min), 0, 1);
    const bh = Math.max(1, t * h);
    const x = left + i * bw;
    const y = top + h - bh;
    const isHover = i === hoverIndex;
    ctx.fillStyle = isHover ? "rgba(255,255,255,0.95)" : color;
    ctx.fillRect(x, y, Math.max(1, bw), bh);
  }

  if (hoverIndex >= 0 && hoverIndex < values.length) {
    const hx = left + (hoverIndex + 0.5) * bw;
    ctx.fillStyle = "rgba(255,255,255,0.30)";
    ctx.fillRect(hx, top, 1, h);
  }

  return { left, top, right, bottom, w, h, bw, count: values.length };
}

function chartCard(id, key, title, unit, metric, color) {
  const min = Number.isFinite(metric?.min) ? metric.min.toFixed(2) : "--";
  const max = Number.isFinite(metric?.max) ? metric.max.toFixed(2) : "--";
  const avg = Number.isFinite(metric?.avg) ? metric.avg.toFixed(2) : "--";
  return `
    <div class="card" style="padding:8px 10px; margin-top:8px;">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:6px;">
        <div class="lm-title" style="margin:0;">${esc(title)}</div>
        <div class="small">min ${esc(min)} · avg ${esc(avg)} · max ${esc(max)} ${esc(unit)}</div>
      </div>
      <canvas id="${esc(id)}" data-chart-key="${esc(key)}" data-unit="${esc(unit)}" data-color="${esc(color)}" style="width:100%; display:block; border:1px solid rgba(148,163,184,0.25); border-radius:8px; background:rgba(2,6,23,0.2);"></canvas>
      <div class="small" id="hist-readout-${esc(key)}" style="margin-top:4px;">Hover chart to inspect daily climatology value</div>
    </div>
  `;
}

function statLine(label, metric = {}, unit = "") {
  const min = Number.isFinite(metric?.min) ? metric.min.toFixed(2) : "--";
  const avg = Number.isFinite(metric?.avg) ? metric.avg.toFixed(2) : "--";
  const max = Number.isFinite(metric?.max) ? metric.max.toFixed(2) : "--";
  return `${label}: min ${min} ${unit}, avg ${avg} ${unit}, max ${max} ${unit}`.trim();
}

function updateReadout(containerEl, key, idx, values, unit) {
  const el = containerEl?.querySelector?.(`#hist-readout-${key}`);
  if (!el) return;
  if (!Number.isFinite(idx) || idx < 0 || idx >= values.length) {
    el.textContent = "Hover chart to inspect daily climatology value";
    return;
  }
  el.textContent = `${formatDayLabel(idx)} · ${formatHoverValue(values[idx], unit)}`;
}

export const historicalWeatherTool = {
  id: "historical-weather",
  name: "Historical Weather",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};

function createRuntime(app) {
  let containerEl = null;
  let state = readState();
  let loading = false;
  let status = "Pick a point and run query.";
  let pickMode = false;
  let result = null;
  const markerLayer = L.layerGroup();
  let marker = null;
  let mapClickHandler = null;
  const hover = { temp: -1, wind: -1, wave: -1, daylight: -1 };
  const chartLayouts = new Map();

  function setMarker(lat, lon) {
    if (!marker) {
      marker = L.circleMarker([lat, lon], {
        radius: 6,
        color: "#38bdf8",
        weight: 2,
        fillColor: "#0ea5e9",
        fillOpacity: 0.65,
      });
      marker.addTo(markerLayer);
    } else {
      marker.setLatLng([lat, lon]);
    }
  }

  function setMapCursor() {
    const map = app.map?.instance;
    const host = map?.getContainer?.();
    if (!host) return;
    host.style.cursor = pickMode ? "crosshair" : "";
  }

  function pullState() {
    if (!containerEl) return;
    const lat = clamp(Number(containerEl.querySelector("#hist-lat")?.value || state.lat), -90, 90);
    const lon = clamp(Number(containerEl.querySelector("#hist-lon")?.value || state.lon), -180, 180);
    const nowYear = new Date().getUTCFullYear();
    let startYear = clamp(Math.round(Number(containerEl.querySelector("#hist-start-year")?.value || state.startYear)), 1940, nowYear);
    let endYear = clamp(Math.round(Number(containerEl.querySelector("#hist-end-year")?.value || state.endYear)), 1940, nowYear);
    if (startYear > endYear) [startYear, endYear] = [endYear, startYear];
    state = { lat, lon, startYear, endYear };
    saveState(state);
  }

  function drawCharts() {
    if (!containerEl || !result) return;
    chartLayouts.clear();
    CHART_SPECS.forEach((spec) => {
      const data = result[spec.key];
      if (!data) return;
      const canvas = containerEl.querySelector(`#${spec.id}`);
      const layout = drawBars(canvas, data.values, {
        color: spec.color,
        unit: spec.unit,
        axis: spec.axis,
        hoverIndex: hover[spec.key],
      });
      if (layout) chartLayouts.set(spec.key, layout);
      updateReadout(containerEl, spec.key, hover[spec.key], data.values, spec.unit);
    });
  }

  function render() {
    if (!containerEl) return;
    const summary = result
      ? `<div class="small" style="margin-top:8px;">Computed from ${esc(String(state.startYear))}-${esc(String(state.endYear))} at (${state.lat.toFixed(4)}, ${state.lon.toFixed(4)}), UTC daily aggregates.</div>`
      : "";
    const proximity = result?.source
      ? `<div class="small" style="margin-top:4px;">Data proximity: weather grid ${esc(formatDistanceNm(result.source.weatherOffsetKm))} · marine grid ${esc(formatDistanceNm(result.source.marineOffsetKm))} from selected point.</div>`
      : "";

    containerEl.innerHTML = `
      <div class="card">
        <div class="lm-title">Historical Weather Climatology</div>
        <div class="small" style="margin:6px 0 10px;">
          Queries Open-Meteo historical weather + marine archives, then computes 365 day-of-year average bars.
        </div>

        <div class="row lm-add-row" style="display:grid; grid-template-columns: 56px 1fr;">
          <label class="small" for="hist-lat">Lat</label>
          <input id="hist-lat" type="number" step="0.0001" min="-90" max="90" value="${state.lat}" />
        </div>
        <div class="row lm-add-row" style="display:grid; grid-template-columns: 56px 1fr; margin-top:6px;">
          <label class="small" for="hist-lon">Lon</label>
          <input id="hist-lon" type="number" step="0.0001" min="-180" max="180" value="${state.lon}" />
        </div>
        <div class="row lm-add-row" style="display:grid; grid-template-columns: 56px 1fr; margin-top:6px;">
          <label class="small" for="hist-start-year">From</label>
          <input id="hist-start-year" type="number" min="1940" max="${new Date().getUTCFullYear()}" value="${state.startYear}" />
        </div>
        <div class="row lm-add-row" style="display:grid; grid-template-columns: 56px 1fr; margin-top:6px;">
          <label class="small" for="hist-end-year">To</label>
          <input id="hist-end-year" type="number" min="1940" max="${new Date().getUTCFullYear()}" value="${state.endYear}" />
        </div>
        <div class="row lm-add-row" style="margin-top:8px;">
          <button id="hist-pick-map" class="btn">${pickMode ? "Click map now..." : "Pick From Map"}</button>
          <button id="hist-run" class="btn" ${loading ? "disabled" : ""}>${loading ? "Loading..." : "Run 365-Day Query"}</button>
          <button id="hist-export-pdf" class="btn" ${!result ? "disabled" : ""}>Export PDF</button>
        </div>

        <div class="small" style="margin-top:10px; min-height:16px;">${esc(status)}</div>
        ${summary}
        ${proximity}
      </div>

      ${result ? chartCard("hist-chart-temp", "temp", "Temperature", "C", result.temp.stats, "#f97316") : ""}
      ${result ? chartCard("hist-chart-wind", "wind", "Wind Speed", "kt", result.wind.stats, "#22c55e") : ""}
      ${result ? chartCard("hist-chart-wave", "wave", "Wave Height", "ft", result.wave.stats, "#38bdf8") : ""}
      ${result ? chartCard("hist-chart-daylight", "daylight", "Daylight Duration", "hours", result.daylight.stats, "#eab308") : ""}
    `;

    setMapCursor();
    if (result) requestAnimationFrame(drawCharts);
  }

  async function exportPdfReport() {
    if (!result) {
      app.ui.toast("Run a query before exporting");
      return;
    }
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 28;
      let y = margin;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("Historical Weather Climatology Report", margin, y);
      y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toISOString()}`, margin, y);
      y += 14;
      doc.text(`Location: ${state.lat.toFixed(5)}, ${state.lon.toFixed(5)}`, margin, y);
      y += 14;
      doc.text(`Period: ${state.startYear} to ${state.endYear}`, margin, y);
      y += 14;
      if (result?.source) {
        doc.text(`Grid proximity: weather ${formatDistanceNm(result.source.weatherOffsetKm)} · marine ${formatDistanceNm(result.source.marineOffsetKm)}`, margin, y);
        y += 14;
      }
      y += 4;

      const addStats = (line) => {
        doc.setFontSize(9);
        doc.text(line, margin, y);
        y += 12;
      };
      addStats(statLine("Temperature", result.temp?.stats, "F"));
      addStats(statLine("Wind Speed", result.wind?.stats, "kt"));
      addStats(statLine("Wave Height", result.wave?.stats, "ft"));
      addStats(statLine("Daylight", result.daylight?.stats, "hr"));
      y += 6;

      for (const spec of CHART_SPECS) {
        const canvas = containerEl?.querySelector?.(`#${spec.id}`);
        if (!canvas) continue;
        const img = canvas.toDataURL("image/png");
        const maxW = pageW - (margin * 2);
        const targetW = maxW;
        const aspect = canvas.width > 0 ? (canvas.height / canvas.width) : (132 / 720);
        const targetH = Math.max(90, targetW * aspect);
        if (y + 16 + targetH > pageH - margin) {
          doc.addPage();
          y = margin;
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(`${spec.title} (${spec.unit})`, margin, y);
        y += 8;
        doc.addImage(img, "PNG", margin, y, targetW, targetH, undefined, "FAST");
        y += targetH + 16;
      }

      doc.save(`historical_weather_${Date.now()}.pdf`);
      app.ui.toast("Historical weather PDF exported");
    } catch (err) {
      console.error("[HistoricalWeather] PDF export failed", err);
      app.ui.toast("Historical weather PDF export failed");
    }
  }

  async function runQuery() {
    pullState();
    setMarker(state.lat, state.lon);
    loading = true;
    status = "Querying historical weather...";
    render();

    let startDate = `${state.startYear}-01-01`;
    let endDate = `${state.endYear}-12-31`;
    // Open-Meteo archive rejects future end_date values (HTTP 400).
    const now = new Date();
    const latestArchiveDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1));
    const latestArchiveDateStr = fmtUtcDate(latestArchiveDate);
    if (endDate > latestArchiveDateStr) endDate = latestArchiveDateStr;
    if (startDate > endDate) startDate = endDate;
    const q1 = new URLSearchParams({
      latitude: String(state.lat),
      longitude: String(state.lon),
      start_date: startDate,
      end_date: endDate,
      timezone: "UTC",
      daily: "temperature_2m_mean,wind_speed_10m_max,daylight_duration",
      wind_speed_unit: "kmh",
      temperature_unit: "celsius",
    });
    const q2 = new URLSearchParams({
      latitude: String(state.lat),
      longitude: String(state.lon),
      start_date: startDate,
      end_date: endDate,
      timezone: "UTC",
      daily: "wave_height_max",
      cell_selection: "sea",
    });
    const weatherUrl = `https://archive-api.open-meteo.com/v1/archive?${q1.toString()}`;
    const marineUrl = `https://marine-api.open-meteo.com/v1/marine?${q2.toString()}`;

    try {
      const [weatherRes, marineRes] = await Promise.all([fetch(weatherUrl), fetch(marineUrl)]);
      if (!weatherRes.ok) throw new Error(`Weather HTTP ${weatherRes.status}`);
      if (!marineRes.ok) throw new Error(`Marine HTTP ${marineRes.status}`);
      const weather = await weatherRes.json();
      const marine = await marineRes.json();

      const tempC = aggregateDailyTo365(weather?.daily?.time, weather?.daily?.temperature_2m_mean);
      const windKmh = aggregateDailyTo365(weather?.daily?.time, weather?.daily?.wind_speed_10m_max);
      const daylightSecs = aggregateDailyTo365(weather?.daily?.time, weather?.daily?.daylight_duration);
      const waveM = aggregateDailyTo365(marine?.daily?.time, marine?.daily?.wave_height_max);
      const temp = tempC.map((v) => (Number.isFinite(v) ? (v * C_TO_F_SCALE + C_TO_F_OFFSET) : null));
      const wind = windKmh.map((v) => (Number.isFinite(v) ? (v * KMH_TO_KT) : null));
      const wave = waveM.map((v) => (Number.isFinite(v) ? (v * M_TO_FT) : null));
      const daylight = daylightSecs.map((v) => (Number.isFinite(v) ? (v / 3600) : null));

      result = {
        temp: { values: temp, stats: stats(temp) },
        wind: { values: wind, stats: stats(wind) },
        wave: { values: wave, stats: stats(wave) },
        daylight: { values: daylight, stats: stats(daylight) },
        source: {
          weatherOffsetKm: Number.isFinite(Number(weather?.latitude)) && Number.isFinite(Number(weather?.longitude))
            ? haversineKm(state.lat, state.lon, Number(weather.latitude), Number(weather.longitude))
            : null,
          marineOffsetKm: Number.isFinite(Number(marine?.latitude)) && Number.isFinite(Number(marine?.longitude))
            ? haversineKm(state.lat, state.lon, Number(marine.latitude), Number(marine.longitude))
            : null,
        },
      };
      Object.keys(hover).forEach((k) => { hover[k] = -1; });
      status = `Query complete (${startDate} to ${endDate}).`;
    } catch (err) {
      console.error("[HistoricalWeather] query failed", err);
      status = `Query failed: ${err?.message || String(err)}`;
      result = null;
      app.ui.toast("Historical weather query failed");
    } finally {
      loading = false;
      render();
    }
  }

  function onCanvasMove(e) {
    if (!result) return;
    const canvas = e.target?.closest?.("canvas[data-chart-key]");
    if (!canvas) return;
    const key = canvas.dataset.chartKey;
    const layout = chartLayouts.get(key);
    const data = result[key];
    if (!layout || !data) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x < layout.left || x > (layout.left + layout.w) || y < layout.top || y > (layout.top + layout.h)) {
      if (hover[key] !== -1) {
        hover[key] = -1;
        drawCharts();
      }
      return;
    }
    const idx = clamp(Math.floor((x - layout.left) / layout.bw), 0, data.values.length - 1);
    if (hover[key] !== idx) {
      hover[key] = idx;
      drawCharts();
    }
  }

  function onCanvasLeave(e) {
    const canvas = e.target?.closest?.("canvas[data-chart-key]");
    if (!canvas) return;
    const key = canvas.dataset.chartKey;
    if (!Object.prototype.hasOwnProperty.call(hover, key)) return;
    if (hover[key] !== -1) {
      hover[key] = -1;
      drawCharts();
    }
  }

  function onClick(e) {
    if (e.target?.closest?.("#hist-run")) {
      runQuery();
      return;
    }
    if (e.target?.closest?.("#hist-pick-map")) {
      pickMode = true;
      status = "Click the map to set query point.";
      render();
      return;
    }
    if (e.target?.closest?.("#hist-export-pdf")) {
      exportPdfReport();
      return;
    }
  }

  function onInput() {
    pullState();
    setMarker(state.lat, state.lon);
  }

  function startMapHandlers() {
    const map = app.map?.instance;
    if (!map) return;
    markerLayer.addTo(map);
    setMarker(state.lat, state.lon);
    mapClickHandler = (ev) => {
      if (!pickMode) return;
      const ll = ev?.latlng;
      if (!ll) return;
      state.lat = clamp(Number(ll.lat), -90, 90);
      state.lon = clamp(Number(ll.lng), -180, 180);
      saveState(state);
      pickMode = false;
      status = `Point set from map: ${state.lat.toFixed(5)}, ${state.lon.toFixed(5)}`;
      setMarker(state.lat, state.lon);
      render();
    };
    map.on("click", mapClickHandler);
  }

  function stopMapHandlers() {
    const map = app.map?.instance;
    if (map && mapClickHandler) map.off("click", mapClickHandler);
    mapClickHandler = null;
    try { markerLayer.remove(); } catch {}
    const host = map?.getContainer?.();
    if (host) host.style.cursor = "";
  }

  function mount(el) {
    containerEl = el;
    startMapHandlers();
    render();
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("mousemove", onCanvasMove);
    containerEl.addEventListener("mouseleave", onCanvasLeave, true);
    window.addEventListener("resize", drawCharts);
  }

  function destroy() {
    stopMapHandlers();
    if (!containerEl) return;
    containerEl.removeEventListener("click", onClick);
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("mousemove", onCanvasMove);
    containerEl.removeEventListener("mouseleave", onCanvasLeave, true);
    window.removeEventListener("resize", drawCharts);
    containerEl = null;
  }

  return { mount, destroy };
}


